import * as React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Spinner } from '@ixicare/ui';
import { Device, DeviceConfigurationCommonResponse, DeviceType, ProfileType } from '@ixicare/resources';
import connect from '../../../store/connect';
import DevicesStore from '~modules/devices/store/Devices';
import AuthStore from '~store/Auth';
import PermissionsStore from '~store/Permissions';
import NotificationsStore from '~store/Notifications';
import { UserAction } from '~config/toastNotification';
import { t } from '~i18n';
import { failureMessage } from '~utils/error';
import { FieldValues, Role } from '../components/DeviceConfigForm/schema';
import { GatingContext } from '../components/DeviceConfigForm/utils/gating';
import { useSiteConfigValues } from '../hooks/useSiteConfigValues';
import { State } from '~store/types';
import ErrorPage from '~/pages/ErrorPage';
import DeviceConfigForm from '../components/DeviceConfigForm';
import { SettingsDiffering } from '../components/DeviceConfigForm/DeviceConfigForm';

interface Props {
  devicesStore: DevicesStore;
  authStore: AuthStore;
  permissions: PermissionsStore;
  notifications: NotificationsStore;
}

const profileTypeToRole = (pt: ProfileType | null | undefined): Role => {
  switch (pt) {
    case ProfileType.WatcherrEmployee:
      return 'WATCHERR_EMPLOYEE';
    case ProfileType.Installer:
      return 'INSTALLER';
    default:
      return 'INSTALLER';
  }
};

const DeviceBulkConfigContainer: React.FC<Props> = ({ devicesStore, authStore, permissions, notifications }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const uuids = React.useMemo(() => (searchParams.get('devices') ?? '').split(',').filter(Boolean), [searchParams]);
  const deviceType = searchParams.get('type') as DeviceType | null;
  const siteConfigValues = useSiteConfigValues(deviceType);
  // No `ownLayer`: the bulk form writes none, so the site is only what the selection is compared
  // against rather than a layer beneath anything.
  const configLayers = React.useMemo(() => ({ site: siteConfigValues }), [siteConfigValues]);

  const [common, setCommon] = React.useState<FieldValues | null>(null);
  const [macs, setMacs] = React.useState<string[]>([]);
  const [differing, setDiffering] = React.useState<SettingsDiffering>({ byKey: {}, macById: {} });
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);

  // Fetch the settings common to the selected devices.
  React.useEffect(() => {
    if (uuids.length === 0) return;
    let cancelled = false;
    setLoading(true);
    setError(false);
    Device.getDevicesSettings({ deviceUuid: uuids, listCommon: true })
      .then((res) => {
        if (cancelled) return;
        const resp = res as DeviceConfigurationCommonResponse;
        setCommon(resp.common ?? {});
        setMacs(Object.values(resp.devices ?? {}).map((d) => d.mac_address));
        setDiffering({
          byKey: resp.differing ?? {},
          macById: Object.fromEntries(Object.entries(resp.devices ?? {}).map(([id, d]) => [id, d.mac_address])),
        });
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [uuids]);

  // Load the schema for the (single) device type of the selection. No firmware version: a
  // selection can span versions, and an ungated superset is safer than hiding a field some of the
  // selected devices do support.
  React.useEffect(() => {
    if (!authStore.userType || !deviceType) return;
    devicesStore.reset();
    devicesStore.loadConfigSchema(deviceType);
  }, [authStore.userType, deviceType, devicesStore]);

  if (!permissions.has('can_edit_all_devices')) {
    return <ErrorPage statusCode={404} />;
  }

  if (uuids.length === 0 || !deviceType) {
    return <Alert type="error">{t('device-config.error.load', { message: 'No devices selected.' })}</Alert>;
  }

  if (devicesStore.schemaError || error) {
    return (
      <Alert type="error">{t('device-config.error.load', { message: 'Could not load device configuration.' })}</Alert>
    );
  }

  if (devicesStore.schemaLoading || !devicesStore.schema || loading || common === null) {
    return <Spinner />;
  }

  const gating: GatingContext = {
    role: profileTypeToRole(authStore.userType),
    permissions: permissions.names,
  };
  const isInstaller = authStore.userType === ProfileType.Installer;

  const onSubmit = async (payload: FieldValues) => {
    const results = await Promise.allSettled(uuids.map((u) => Device.postDeviceSettings(u, payload)));
    const failures = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected');
    if (failures.length) {
      notifications.addUserActionNotification({
        type: UserAction.DeviceConfigUpdate,
        notificationType: 'error',
        message: failureMessage(failures[0].reason, t('alerts.device.config.update.failure')),
      });
      // Keep the form dirty so the user can retry; don't navigate away.
      throw new Error('Bulk settings update failed');
    }
    notifications.addUserActionNotification({
      type: UserAction.DeviceConfigUpdate,
      notificationType: 'success',
      message: t('alerts.device.config.update.success'),
    });
    navigate('/device-overview');
  };

  return (
    <DeviceConfigForm
      schema={devicesStore.schema}
      title={t('device-config.page.title')}
      subtitle={t(devicesStore.schema.meta.subtitle_key)}
      configValues={common}
      scope={{ kind: 'bulk', macs, differing }}
      isInstaller={isInstaller}
      gating={gating}
      onSubmit={onSubmit}
      configLayers={configLayers}
    />
  );
};

export default connect((state: State) => ({
  devicesStore: state.devices,
  authStore: state.auth,
  permissions: state.permissions,
  notifications: state.notifications,
}))(DeviceBulkConfigContainer);
